import jsPDF from "jspdf";
import "jspdf-autotable";
import { parseHeader, drawReportHeader } from "../../../../util/headerUtil";
import { themeColors } from "../../../../util/themeColors";

export const exportTreatmentPDF = (patientData, treatments, authCtx, format = "a4") => {
    const hospitalData = authCtx?.hospitalData;
    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4" // Enforce A4
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;

    // Use Standardized Report Header
    const startY = drawReportHeader(doc, hospitalData, "", {
        hideSeparator: true,
        isExport: false,
        margin: 10,
        top: 0
    });

    let currentY = startY + 5;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.text("IPD TREATMENT SHEET", pageWidth / 2, currentY, { align: "center" });
    currentY += 10;

    // --- Patient Information ---
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Name: ${(patientData.name || "").toUpperCase()}`, margin, currentY);
    doc.text(`Adm. Date: ${patientData.admissionDate || "-"} ${patientData.admissionTime || ""}`, pageWidth - margin, currentY, { align: "right" });
    currentY += 6;
    doc.setFont("helvetica", "normal");
    doc.text(`Age / Gender: ${patientData.age || "-"} YRS / ${patientData.gender || "-"}`, margin, currentY);
    doc.text(`IPD No: ${patientData.ipdAdmissionId || "-"}`, pageWidth - margin, currentY, { align: "right" });
    currentY += 6;
    doc.text(`Contact: ${patientData.phone || "-"}`, margin, currentY);
    doc.text(`Doctor: ${(patientData.doctor || "-").toUpperCase()}`, pageWidth - margin, currentY, { align: "right" });
    currentY += 6;
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;


    // --- Main Table ---
    const tableData = treatments.map(t => {
        let datePart = "-";
        let timePart = "-";
        if (t.vitalDateTime) {
            if (t.vitalDateTime.includes("T")) [datePart, timePart] = t.vitalDateTime.split("T");
            else if (t.vitalDateTime.includes(" ")) [datePart, timePart] = t.vitalDateTime.split(" ");
            else datePart = t.vitalDateTime;
        } else {
            datePart = t.date || "-";
            timePart = t.time || "-";
        }
        const dateTime = `${datePart}\n${timePart.slice(0, 5)}`;
        const vitalsStr = `BP: ${t.bpSystolic || "-"}/${t.bpDiastolic || "-"}  T: ${t.temperature || t.temp || "-"}  P: ${t.pulse || "-"}\nSpO2: ${t.spo2 || "-"}%  WT: ${t.weight || "-"} kg`;

        let meds = "-";
        if (Array.isArray(t.details) && t.details.length > 0) {
            meds = t.details
                .filter(d => d.name)
                .map(d => `${d.type || ''} ${d.name} (${d.dose || '-'}) ${d.instructions ? '\n' + d.instructions : ''}`)
                .join("\n\n");
        } else if (typeof t.details === "string" && t.details !== "N/A") {
            meds = t.details;
        }

        if (t.remarks && t.remarks !== "-") meds = `R: ${t.remarks}\n\n${meds}`;

        return [dateTime, vitalsStr, meds];
    });

    const themeName = hospitalData?.applicationThemeColour || "TEAL";
    const theme = themeColors[themeName] || themeColors.TEAL;
    const hexToRgb = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b];
    };
    const primaryRgb = hexToRgb(theme.primary);
    const secondaryRgb = hexToRgb(theme.secondary);

    doc.autoTable({
        startY: currentY,
        head: [["DATE & TIME", "VITALS", "MEDICATION / NOTES"]],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: [10, 10, 10] },
        headStyles: {
            fillColor: secondaryRgb,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: primaryRgb
        },
        columnStyles: {
            0: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 65 },
            2: { cellWidth: 'auto' }
        },
        margin: { left: margin, right: margin, bottom: 30 }
    });

    // --- Footer Signatures ---
    const footerY = pageHeight - 30;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    doc.line(margin, footerY, margin + 50, footerY);
    doc.text("Patient/Guardian Signature", margin + 25, footerY + 5, { align: "center" });

    const rightStart = pageWidth - margin - 50;
    doc.line(rightStart, footerY, pageWidth - margin, footerY);
    doc.text("Authority Sign / Doctor Sign", pageWidth - margin - 25, footerY + 5, { align: "center" });

    const userRole = authCtx?.role;
    const userName = authCtx?.userName;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    if (userRole === "ROLE_DOCTOR" && userName) {
        doc.text(`${userName}`, pageWidth - margin - 25, footerY + 10, { align: "center" });
    }

    const safeName = (patientData.name || "patient").replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`${safeName}_TreatmentHistory.pdf`);
};
